import React from 'react';
import { TableRow, Box, Grid2 } from '@mui/material';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import DeleteIcon from '@assets/images/delete_table.svg';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomCheckbox from '@ui-components/CheckBox/CustomCheckBox';
import DragIcon from '@assets/images/drag-icon.svg';
import CustomSelect from '@ui-components/Select/CustomSelect';
import type {
  FieldTypes,
  AdditionalSessionFieldRowData,
} from '../../API/services/IntakeFormService';
import type { FormikErrors, FormikTouched } from 'formik';
export type AdditionalSessionFieldErrors = {
  fieldName?: string;
  fieldType?: string;
  fieldValue?: string;
  displayOnTile?: string;
  mandatory?: string;
  error?: string;
};

interface AdditionalSessionFieldProps {
  additinalSessionFields: AdditionalSessionFieldRowData[];
  setAdditinalSessionFields: React.Dispatch<
    React.SetStateAction<AdditionalSessionFieldRowData[]>
  >;
  AdditionalSessionFieldsTypeddl: FieldTypes[];
  errors?: FormikErrors<AdditionalSessionFieldRowData>[] | string | string[];

  touched?: FormikTouched<AdditionalSessionFieldRowData>[] | boolean;
}

// Sortable Row
const SortableRow: React.FC<{
  row: AdditionalSessionFieldRowData;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  AdditionalSessionFieldsTypeddl: FieldTypes[];
  onChange: (
    id: number,
    key: keyof AdditionalSessionFieldRowData,
    value: any
  ) => void;
  onDelete: (id: number) => void;
  errors?: AdditionalSessionFieldErrors;
  touched?: Partial<Record<keyof AdditionalSessionFieldErrors, boolean>>;
}> = ({
  row,
  onChange,
  onDelete,
  AdditionalSessionFieldsTypeddl,
  errors,
  touched,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const selectedFieldType = AdditionalSessionFieldsTypeddl.find(
    (type) => type.id === row.fieldType
  );
  const isTextFieldEnabled =
    selectedFieldType?.name?.toLowerCase() === 'dropdown' ||
    selectedFieldType?.name?.toLowerCase() === 'checkbox';

  return (
    <TableRow id="sortable-row" ref={setNodeRef} style={style}>
      {/* Drag handle */}
      <CustomTableCell
        id="drag-handle-cell"
        {...attributes}
        {...listeners}
        className="width-5"
      >
        <CustomIconButton
          id="drag-icon"
          size="small"
          icon={<DragIcon />}
        ></CustomIconButton>
      </CustomTableCell>
      {/* Editable cells */}
      <CustomTableCell id="field-name-cell">
        <CustomTextField
          id="field-name-cell"
          variant="outlined"
          size="small"
          value={row.fieldName}
          onChange={(e) => onChange(row.id, 'fieldName', e.target.value)}
          placeholder="Enter field name"
        />
        {errors?.fieldName && touched?.fieldName && (
          <CustomTypography
            id="validate-field-name"
            color="error"
            variant="body3"
            className="pt-8"
          >
            {errors.fieldName}
          </CustomTypography>
        )}
      </CustomTableCell>
      <CustomTableCell id="field-type-cell">
        <CustomSelect
          id="field-type-cell"
          variant="outlined"
          size="small"
          options={AdditionalSessionFieldsTypeddl}
          selectedValue={row.fieldType}
          placeholder="Choose"
          displayEmpty={true}
          fullWidth
          onChange={(selectedValue) => {
            onChange(row.id, 'fieldType', selectedValue);
          }}
        ></CustomSelect>
        {errors?.fieldType && touched?.fieldType && (
          <CustomTypography
            id="validate-field-type"
            color="error"
            variant="body3"
            className="pt-8"
          >
            {errors.fieldType}
          </CustomTypography>
        )}
      </CustomTableCell>
      <CustomTableCell id="field-value-cell">
        <CustomTextField
          id="field-value-cell"
          variant="outlined"
          size="small"
          value={row.fieldValue}
          placeholder="Ex. Value 1, Value 2..."
          onChange={(e) => onChange(row.id, 'fieldValue', e.target.value)}
          disabled={!isTextFieldEnabled}
        />
        {errors?.fieldValue && touched?.fieldValue && (
          <CustomTypography
            id="validate-field-value"
            color="error"
            variant="body3"
            className="pt-8"
          >
            {errors.fieldValue}
          </CustomTypography>
        )}
      </CustomTableCell>
      <CustomTableCell
        id="display-on-tile-cell"
        className="justify-content-center"
      >
        <CustomCheckbox
          id="display-on-tile"
          size="medium"
          checked={row.displayOnTile}
          onChange={(e) => onChange(row.id, 'displayOnTile', e.target.checked)}
        />
      </CustomTableCell>
      <CustomTableCell id="mandatory-cell" className="justify-content-center">
        <CustomCheckbox
          id="mandatory"
          size="medium"
          checked={row.mandatory}
          onChange={(e) => onChange(row.id, 'mandatory', e.target.checked)}
        />
      </CustomTableCell>
      {/* Delete button */}
      <CustomTableCell id="action-cell" className="width-10">
        <CustomIconButton
          id="delete-icon"
          size="small"
          icon={<DeleteIcon />}
          color="error"
          onClick={() => onDelete(row.id)}
        ></CustomIconButton>
      </CustomTableCell>
    </TableRow>
  );
};

const AdditionalSessionFields: React.FC<AdditionalSessionFieldProps> = ({
  additinalSessionFields,
  setAdditinalSessionFields,
  AdditionalSessionFieldsTypeddl,
  errors,
  touched,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = additinalSessionFields.findIndex(
        (row) => row.id === active.id
      );
      const newIndex = additinalSessionFields.findIndex(
        (row) => row.id === over.id
      );
      setAdditinalSessionFields((items) =>
        arrayMove(items, oldIndex, newIndex)
      );
    }
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleChange = (
    id: number,
    key: keyof AdditionalSessionFieldRowData,
    value: any
  ) => {
    setAdditinalSessionFields((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  };

  const handleAddRow = () => {
    const maxId =
      additinalSessionFields.length > 0
        ? Math.max(...additinalSessionFields.map((r) => r.id))
        : 0;
    const newRow: AdditionalSessionFieldRowData = {
      id: maxId + 1,
      additionalSessionId: 0,
      fieldName: '',
      fieldType: 0,
      fieldValue: '',
      displayOnTile: false,
      mandatory: false,
    };
    setAdditinalSessionFields((prev) => [...prev, newRow]);
  };

  const handleDeleteRow = (id: number) => {
    setAdditinalSessionFields((prev) =>
      prev
        .filter((row) => row.id !== id)
        .map((row, index) => ({ ...row, id: index + 1 }))
    );
  };

  return (
    <Box>
      <Box className="pb-20">
        <Grid2 container>
          <Grid2
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
            className="flex align-items-center"
          >
            <Box>
              <CustomTypography id="facility-fields-title" variant="h5">
                Additional Information
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
            className="align-items-center text-align-right"
          >
            <Box className="">
              <CustomButton
                id="add-field-button"
                variant="contained"
                onClick={handleAddRow}
              >
                Add Field
              </CustomButton>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box>
        <Grid2 container className="pb-32">
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            {/* <TableContainer> */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={additinalSessionFields.map((r) => r.id)}
                strategy={verticalListSortingStrategy}
              >
                <CustomTable id="facility-fields-table">
                  <CustomTableHead id="facility-fields-table-head">
                    <CustomTableRow id="facility-fields-table-header-row">
                      <CustomTableCell
                        id="intake-sorting-column"
                        className="width-5"
                      ></CustomTableCell>
                      <CustomTableCell id="intake-field-name-column">
                        FIELD NAME
                      </CustomTableCell>
                      <CustomTableCell id="intake-field-type-column">
                        FIELD TYPE
                      </CustomTableCell>
                      <CustomTableCell id="intake-field-Value-column">
                        FIELD VALUE
                      </CustomTableCell>
                      <CustomTableCell id="intake-display-on-tile-column">
                        DISPLAY TO CLINICIAN
                      </CustomTableCell>
                      <CustomTableCell id="intake-mandatory-column">
                        MANDATORY
                      </CustomTableCell>
                      <CustomTableCell id="intake-actions-column">
                        ACTIONS
                      </CustomTableCell>
                    </CustomTableRow>
                  </CustomTableHead>
                  {additinalSessionFields.length > 0 ? (
                    <CustomTableBody id="facility-fields-table-body">
                      {additinalSessionFields.map((row, index) => (
                        <SortableRow
                          key={row.id}
                          row={row}
                          onChange={handleChange}
                          onDelete={handleDeleteRow}
                          AdditionalSessionFieldsTypeddl={
                            AdditionalSessionFieldsTypeddl
                          }
                          errors={
                            typeof errors?.[index] === 'string'
                              ? { error: errors[index] } // convert string → object
                              : (errors?.[
                                  index
                                ] as AdditionalSessionFieldErrors)
                          }
                          touched={
                            touched && Array.isArray(touched)
                              ? touched[index]
                              : undefined
                          }
                        />
                      ))}
                    </CustomTableBody>
                  ) : (
                    <CustomTableBody id="session-fields-table-body">
                      <CustomTableRow id="no-data-row">
                        <CustomTableCell id="no-data-cell" colspan={7}>
                          <Box className="flex flex-justify-center align-items-center">
                            <CustomTypography
                              id="no-data-cell-text"
                              variant="body3"
                              color="textDisabled"
                            >
                              No Additional Information Added
                            </CustomTypography>
                          </Box>
                        </CustomTableCell>
                      </CustomTableRow>
                    </CustomTableBody>
                  )}
                </CustomTable>
              </SortableContext>
            </DndContext>
            {/* </TableContainer> */}
          </Grid2>
        </Grid2>
      </Box>
    </Box>
  );
};

export default AdditionalSessionFields;
